// src/pages/itsm/ITSMDashboard.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sun, Moon, Ticket, Hourglass, CheckCircle2, AlertTriangle, Package, ClipboardList, Users } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, statusTone } from '../../components/ui/UI'

export default function ITSMDashboard() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [stats, setStats] = useState({
        open: 0, inProgress: 0, pendingApproval: 0, resolved: 0,
        openPOs: 0, pendingPOs: 0, totalAgents: 0, slaBreached: 0
    })
    const [recentTickets, setRecentTickets] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => { fetchStats() }, [])

    const fetchStats = async () => {
        const [
            { data: tickets },
            { data: pos },
            { data: agents },
        ] = await Promise.all([
            supabase.from('itsm_tickets').select('status, sla_breached, priority'),
            supabase.from('itsm_purchase_orders').select('status'),
            supabase.from('itsm_agents').select('id').eq('is_active', true),
        ])

        const { data: recent } = await supabase
            .from('itsm_tickets')
            .select('*, itsm_services(name), itsm_agents!assigned_agent_id(name)')
            .order('created_at', { ascending: false })
            .limit(5)

        setStats({
            open: tickets?.filter(t => t.status === 'open').length || 0,
            inProgress: tickets?.filter(t => t.status === 'in_progress').length || 0,
            pendingApproval: tickets?.filter(t => t.status === 'pending_approval').length || 0,
            resolved: tickets?.filter(t => t.status === 'resolved').length || 0,
            openPOs: pos?.filter(p => ['draft', 'pending_approval'].includes(p.status)).length || 0,
            pendingPOs: pos?.filter(p => p.status === 'pending_approval').length || 0,
            totalAgents: agents?.length || 0,
            slaBreached: tickets?.filter(t => t.sla_breached).length || 0,
        })
        setRecentTickets(recent || [])
        setLoading(false)
    }

    const s = styles
    const kpis = [
        { label: 'Open Tickets', value: stats.open, tone: 'info', Icon: Ticket, path: '/dashboard/itsm/tickets?status=open' },
        { label: 'In Progress', value: stats.inProgress, tone: 'warning', Icon: Hourglass, path: '/dashboard/itsm/tickets?status=in_progress' },
        { label: 'Pending Approval', value: stats.pendingApproval, tone: 'violet', Icon: Hourglass, path: '/dashboard/itsm/tickets?status=pending_approval' },
        { label: 'Resolved Today', value: stats.resolved, tone: 'success', Icon: CheckCircle2, path: '/dashboard/itsm/tickets?status=resolved' },
        { label: 'SLA Breached', value: stats.slaBreached, tone: 'danger', Icon: AlertTriangle, path: '/dashboard/itsm/tickets' },
        { label: 'Open POs', value: stats.openPOs, tone: 'info', Icon: Package, path: '/dashboard/itsm/po' },
        { label: 'Pending POs', value: stats.pendingPOs, tone: 'warning', Icon: ClipboardList, path: '/dashboard/itsm/po' },
        { label: 'Active Agents', value: stats.totalAgents, tone: 'neutral', Icon: Users, path: '/dashboard/itsm/agents' },
    ]

    const STATUS_CONFIG: Record<string, { label: string }> = {
        open: { label: 'Open' },
        in_progress: { label: 'In Progress' },
        pending_approval: { label: 'Pending Approval' },
        resolved: { label: 'Resolved' },
        closed: { label: 'Closed' },
        pending: { label: 'Pending' },
    }

    const PRIORITY_CONFIG: Record<string, { label: string; tone: 'accent' | 'warning' | 'danger' | 'violet' }> = {
        low: { label: 'Low', tone: 'accent' },
        medium: { label: 'Medium', tone: 'warning' },
        high: { label: 'High', tone: 'danger' },
        urgent: { label: 'Urgent', tone: 'violet' },
    }

    const kpiTone = (tone: string) => tone === 'neutral' ? T.text2 : (T as any)[tone]

    if (loading) return (
        <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>
    )

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            {/* Header */}
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>ITSM Dashboard</h1>
                    <p style={s.subtitle}>IT Service Management — Overview</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer' }}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <button style={s.btnSecondary} onClick={() => navigate('/dashboard/itsm/tickets')}>
                        All Tickets
                    </button>
                    <button style={s.btnPrimary} onClick={() => navigate('/dashboard/itsm/tickets/new')}>
                        + New Ticket
                    </button>
                </div>
            </div>

            {/* KPI Grid */}
            <div style={s.kpiGrid}>
                {kpis.map((k, i) => (
                    <Card key={i} style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }} onClick={() => navigate(k.path)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <p style={s.kpiLabel}>{k.label}</p>
                                <p className="jr-mono" style={{ ...s.kpiValue, color: kpiTone(k.tone) }}>{k.value}</p>
                            </div>
                            <k.Icon size={20} color={kpiTone(k.tone)} />
                        </div>
                    </Card>
                ))}
            </div>

            <div style={s.grid}>
                {/* Recent Tickets */}
                <Card style={{ padding: 20 }}>
                    <div style={s.cardHeader}>
                        <p style={s.cardTitle}>Recent Tickets</p>
                        <button style={s.btnLink} onClick={() => navigate('/dashboard/itsm/tickets')}>View all →</button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                {['Ticket', 'Title', 'Service', 'Priority', 'Status', 'Agent'].map(h => (
                                    <th key={h} style={s.th}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {recentTickets.map(t => {
                                const status = STATUS_CONFIG[t.status] || STATUS_CONFIG.open
                                const priority = PRIORITY_CONFIG[t.priority] || PRIORITY_CONFIG.medium
                                return (
                                    <tr key={t.id} style={s.tr} onClick={() => navigate(`/dashboard/itsm/tickets/${t.id}`)}>
                                        <td style={s.td}>
                                            <span className="jr-mono" style={{ fontSize: 12, color: T.text3, fontWeight: 600 }}>{t.ticket_number}</span>
                                        </td>
                                        <td style={s.td}>
                                            <span style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{t.title}</span>
                                        </td>
                                        <td style={s.td}>
                                            <span style={{ fontSize: 12, color: T.text2 }}>{t.itsm_services?.name || '—'}</span>
                                        </td>
                                        <td style={s.td}>
                                            <span style={{ fontSize: 11, fontWeight: 700, color: priority.tone === 'accent' ? T.text2 : (T as any)[priority.tone] }}>{priority.label}</span>
                                        </td>
                                        <td style={s.td}>
                                            <Pill tone={statusTone(t.status)} icon={undefined} style={undefined}>{status.label}</Pill>
                                        </td>
                                        <td style={s.td}>
                                            <span style={{ fontSize: 12, color: T.text2 }}>{t.itsm_agents?.name || 'Unassigned'}</span>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </Card>

                {/* Quick Actions */}
                <Card style={{ padding: 20 }}>
                    <p style={s.cardTitle}>Quick Actions</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                        {[
                            { label: '🎫 New Ticket', path: '/dashboard/itsm/tickets/new', tone: 'info' },
                            { label: '📦 New Purchase Order', path: '/dashboard/itsm/po/new', tone: 'info' },
                            { label: '📋 Service Catalog', path: '/dashboard/itsm/catalog', tone: 'violet' },
                            { label: '👥 Manage Agents', path: '/dashboard/itsm/agents', tone: 'success' },
                            { label: '🏢 Manage Teams', path: '/dashboard/itsm/teams', tone: 'warning' },
                            { label: '🏪 Vendors', path: '/dashboard/itsm/vendors', tone: 'neutral' },
                            { label: '⚙️ Settings', path: '/dashboard/itsm/settings', tone: 'neutral' },
                            { label: '💻 Assets', path: '/dashboard/itsm/assets', tone: 'info' },
                            { label: '🔄 Changes', path: '/dashboard/itsm/changes', tone: 'info' },
                            { label: '🔴 Problems', path: '/dashboard/itsm/problems', tone: 'danger' },
                            { label: '📚 Knowledge Base', path: '/dashboard/itsm/kb', tone: 'success' },
                            { label: '📊 Reports', path: '/dashboard/itsm/reports', tone: 'violet' },
                        ].map((a, i) => (
                            <button key={i} style={{ ...s.quickAction, borderLeft: `3px solid ${a.tone === 'neutral' ? T.text3 : (T as any)[a.tone]}` }}
                                onClick={() => navigate(a.path)}>
                                {a.label}
                            </button>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    subtitle: { fontSize: 13, color: T.text2 },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
    kpiLabel: { fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
    kpiValue: { fontSize: 32, fontWeight: 700, lineHeight: 1 },
    grid: { display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    cardTitle: { fontSize: 13, fontWeight: 700, color: T.text },
    btnPrimary: { height: 36, padding: '0 18px', background: T.accent, color: T.accentFg, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    btnSecondary: { height: 36, padding: '0 18px', background: 'transparent', color: T.text2, border: `1px solid ${T.borderStrong}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' },
    btnLink: { background: 'none', border: 'none', color: T.info, fontSize: 12, cursor: 'pointer', fontWeight: 500 },
    th: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '8px 12px', textAlign: 'left' as const },
    td: { fontSize: 13, padding: '10px 12px', borderBottom: `1px solid ${T.border}` },
    tr: { cursor: 'pointer' },
    quickAction: { padding: '10px 14px', background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, color: T.text2, cursor: 'pointer', textAlign: 'left' as const, fontWeight: 500 },
}
